/**
 * DBN Workspace — Layout + Sidebar + Role Switching
 * Development Bank of Nigeria — Green, Digital and Inclusive Finance Platform
 */
import React, { useState } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import {
  BarChart3, Building2, GitBranch, FolderOpen, Shield,
  Leaf, FileText, Bot, ChevronRight, AlertCircle, ChevronDown,
  Check, ClipboardCheck, GraduationCap
} from "lucide-react";

const DBN_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_circularity-hub/artifacts/cgq5n4vn_Screenshot%202026-07-31%20at%2002.50.28.png";
const DCI_LOGO = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Navigation — ordered around DBN's financing and impact lifecycle ──────────
const NAV_GROUPS = [
  {
    label: "PORTFOLIO",
    items: [
      { label:"Executive Overview",              path:"/institutions/dbn/overview",              icon:BarChart3      },
      { label:"Development Partner Facilities",  path:"/institutions/dbn/eib-facility",          icon:Building2      },
    ],
  },
  {
    label: "ORIGINATION & INVESTMENT",
    items: [
      { label:"Eligibility & Investment Assessment", path:"/institutions/dbn/eligibility-assessment", icon:ClipboardCheck },
      { label:"PFI & MSME Pipeline",               path:"/institutions/dbn/pipeline",               icon:GitBranch      },
      { label:"Project Portfolio",                 path:"/institutions/dbn/projects",               icon:FolderOpen     },
    ],
  },
  {
    label: "PORTFOLIO SUPPORT & RISK",
    items: [
      { label:"Technical Assistance",      path:"/institutions/dbn/ta-manager",         icon:GraduationCap  },
      { label:"Risk & Additionality",      path:"/institutions/dbn/risk-additionality", icon:Shield         },
    ],
  },
  {
    label: "IMPACT & INTELLIGENCE",
    items: [
      { label:"Reporting & Disclosure",               path:"/institutions/dbn/reporting",   icon:FileText },
      { label:"Environmental Markets & Monetisation", path:"/institutions/dbn/monetisation",icon:Leaf     },
      { label:"AI Copilot",                          path:"/institutions/dbn/ai-copilot",  icon:Bot      },
    ],
  },
];

export const DEMO_ROLES = [
  { id:"dbn_md",   label:"Managing Director",           org:"Development Bank of Nigeria", color:"#34d399" },
  { id:"dbn_sf",   label:"Sustainable Finance Lead",    org:"Development Bank of Nigeria", color:"#60a5fa" },
  { id:"dbn_risk", label:"Risk & ESG Officer",          org:"Development Bank of Nigeria", color:"#a78bfa" },
  { id:"dbn_pfi",  label:"PFI Officer",                 org:"Access Bank Plc",             color:"#fbbf24" },
  { id:"dbn_dp",   label:"Development Partner Observer",org:"European Investment Bank",    color:"#f97316" },
];

export default function DBNLayout() {
  const location = useLocation();
  const [activeRole, setActiveRole] = useState(DEMO_ROLES[0]);
  const [roleOpen, setRoleOpen] = useState(false);

  return (
    <div style={{ display:"flex", height:"100vh", overflow:"hidden", background:"#060f1e" }}>
      {/* Sidebar */}
      <aside style={{
        width:236, flexShrink:0, background:"#07111e",
        borderRight:"1px solid #0f1e33", display:"flex", flexDirection:"column",
        overflowY:"auto"
      }}>
        {/* ── Branding area ── */}
        <div style={{ padding:"14px 14px 10px", borderBottom:"1px solid #0f1e33" }}>
          {/* Official DBN Logo — contained badge */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:6 }}>
            <div style={{
              background:"rgba(255,255,255,0.94)",
              borderRadius:7,
              padding:"7px 14px",
              maxWidth:138,
              boxShadow:"0 1px 6px rgba(0,0,0,0.35)",
              border:"1px solid rgba(255,255,255,0.2)",
            }}>
              <img
                src={DBN_LOGO}
                alt="Development Bank of Nigeria"
                style={{
                  display:"block",
                  width:"100%",
                  maxWidth:110,
                  height:"auto",
                  maxHeight:48,
                  objectFit:"contain",
                }}
              />
            </div>
          </div>

          {/* Powered by label */}
          <p style={{
            fontSize:8, color:"#334155", textTransform:"uppercase",
            letterSpacing:"0.12em", textAlign:"center", marginBottom:10
          }}>
            Powered by DeCarbonIQ
          </p>

          {/* Role Switcher */}
          <div style={{ position:"relative" }}>
            <button
              onClick={() => setRoleOpen(o => !o)}
              data-testid="dbn-role-switcher"
              style={{
                width:"100%", background:"#0a1628", border:"1px solid #1e3a5f",
                borderRadius:8, padding:"7px 10px", cursor:"pointer",
                display:"flex", alignItems:"center", gap:8,
              }}>
              <div style={{ width:8, height:8, borderRadius:"50%", background:activeRole.color, flexShrink:0 }} />
              <div style={{ flex:1, textAlign:"left", overflow:"hidden" }}>
                <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff", lineHeight:1.2,
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {activeRole.label}
                </p>
                <p style={{ fontSize:8, color:"#475569", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {activeRole.org}
                </p>
              </div>
              <ChevronDown size={10} color="#475569"
                style={{ flexShrink:0, transform:roleOpen?"rotate(180deg)":"none", transition:".15s" }} />
            </button>

            {roleOpen && (
              <div style={{
                position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:1000,
                background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:10,
                boxShadow:"0 8px 24px rgba(0,0,0,.7)", overflow:"hidden"
              }}>
                <p style={{ fontSize:8, color:"#334155", textTransform:"uppercase", letterSpacing:"0.1em",
                  padding:"7px 10px 4px", fontWeight:700 }}>Switch Demo Role</p>
                {DEMO_ROLES.map(role => (
                  <button key={role.id}
                    onClick={() => { setActiveRole(role); setRoleOpen(false); }}
                    data-testid={`role-option-${role.id}`}
                    style={{
                      width:"100%", padding:"8px 10px", background:"transparent", border:"none",
                      cursor:"pointer", display:"flex", alignItems:"center", gap:8, textAlign:"left",
                      borderTop:"1px solid #0f2033",
                    }}
                    onMouseEnter={e=>e.currentTarget.style.background="#0f2033"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{ width:7, height:7, borderRadius:"50%", background:role.color, flexShrink:0 }} />
                    <div style={{ flex:1, overflow:"hidden" }}>
                      <p style={{ fontSize:10, fontWeight:600, color:"#f0f9ff", lineHeight:1.2,
                        whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{role.label}</p>
                      <p style={{ fontSize:8, color:"#475569" }}>{role.org}</p>
                    </div>
                    {activeRole.id === role.id && <Check size={10} color="#34d399" flexShrink={0} />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Demo Notice */}
          <div style={{ marginTop:8, background:"#1a1000", borderRadius:6, padding:"4px 8px",
            border:"1px solid #3d2000", display:"flex", alignItems:"center", gap:4 }}>
            <AlertCircle size={9} color="#f59e0b" />
            <p style={{ fontSize:8, color:"#92400e", lineHeight:1.4 }}>Demo — synthetic data only</p>
          </div>
        </div>

        {/* ── Navigation with Group Labels ── */}
        <nav style={{ padding:"6px 0 8px", flex:1 }}>
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <p style={{
                fontSize:8, color:"#1e3a4a", textTransform:"uppercase",
                letterSpacing:"0.14em", padding:"10px 14px 3px", fontWeight:800
              }}>{group.label}</p>
              {group.items.map(({ label, path, icon: Icon }) => {
                const active = location.pathname === path || location.pathname.startsWith(path+"/");
                return (
                  <NavLink key={path} to={path}
                    style={{
                      display:"flex", alignItems:"center", gap:8,
                      padding:"6px 12px", margin:"1px 5px",
                      borderRadius:7, textDecoration:"none",
                      background:active?"rgba(52,211,153,0.11)":"transparent",
                      borderLeft:active?"2px solid #34d399":"2px solid transparent",
                      color:active?"#34d399":"#5a7a8a",
                      fontSize:11, fontWeight:active?600:400,
                      transition:"all .12s",
                    }}
                    onMouseEnter={e=>{ if(!active) e.currentTarget.style.color="#94a3b8"; }}
                    onMouseLeave={e=>{ if(!active) e.currentTarget.style.color="#5a7a8a"; }}
                  >
                    <Icon size={12} strokeWidth={active?2:1.75} style={{ flexShrink:0 }} />
                    <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {label}
                    </span>
                    {active && <ChevronRight size={9} style={{ flexShrink:0 }} />}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* ── DeCarbonIQ Footer — bold white ── */}
        <div style={{ padding:"12px 14px 10px", borderTop:"1px solid #0f1e33" }}>
          <p style={{
            fontSize:7, color:"#2a4a5a", textTransform:"uppercase",
            letterSpacing:"0.16em", marginBottom:6, fontWeight:700
          }}>
            POWERED BY
          </p>
          <img
            src={DCI_LOGO}
            alt="DeCarbonIQ"
            style={{
              height:20, maxWidth:140, objectFit:"contain", display:"block",
              filter:"brightness(0) invert(1)",
              opacity:0.88,
            }}
          />
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
        <Outlet context={{ activeRole }} />
      </main>
    </div>
  );
}
